/**
 * The signed send-back, as the server answers it (docs/record-flow/send-back-design.md §4).
 *
 * One preview (GET) and one act (POST) per workspace, the same shapes in all
 * four. A refusal is `{ success: false, code, message, detail? }`; nothing here
 * turns a refusal into a success or invents a code the server did not send.
 */
import { apiRequest } from '../../services/api';

export type SendBackFlow = 'PA_TO_PA' | 'UMED_TO_PA' | 'FINANCE_TO_UMED' | 'MANIFEST_TO_PROVIDER';

export interface SbResult<T> { success: boolean; data?: T; error?: string; message?: string; code?: string; status?: number; detail?: any }

/** The four preview/act endpoints. The path is the only thing that differs between flows. */
export const sendBackPath = {
  pa: (caseId: string) => `/advocate/cases/${caseId}/send-back`,
  umed: (caseId: string) => `/umed/cases/${caseId}/send-back`,
  finance: (handoffId: string) => `/finance/handoffs/${handoffId}/send-back`,
  manifest: (lineId: string) => `/manifest/lines/${lineId}/send-back`,
};

export interface SendBackTarget {
  userId: string | null;
  employeeId: string | null;
  mondayUserId: string | number | null;
  name: string;
  source: string;
  confidence?: 'EXACT' | 'SOURCE_REPORTED';
  /** UMED to PA: the PA who sent the case to UMED, when the current holder is someone else. */
  originating?: { userId: string | null; name: string };
  /** The recipient has no eMerge login; they get the email only. */
  emailOnly?: boolean;
}

export interface SendBackPreview {
  can: boolean;
  refusal: { code: string; message: string; detail?: any } | null;
  to: SendBackTarget | null;
  fromStep: string;
  holderBasis: string | null;
  version: number | null;
  hasAuthenticator: boolean;
  gateOpen: boolean;
}

export interface SendBackBody {
  reason: string;
  totp_code: string;
  to_user_id?: string;
  to_employee_id?: string;
  to_monday_user_id?: string | number;
  version?: number;
}

export interface SendBackResult {
  sendBackId: string;
  flow: SendBackFlow;
  recordType: string;
  recordId: string;
  chainSeq: number;
  signature: string;
  keyId: string;
  signedAt: string;
  holderBasis: string | null;
  to: SendBackTarget;
  effects: Record<string, unknown>;
  notice: { state: string };
  replayed: boolean;
}

export interface ReturnedRow {
  sendBackId: string;
  flow: SendBackFlow;
  recordType: string;
  recordId: string;
  signedAt: string;
  fromName: string;
  toName: string;
  reason: string;
  fromStep: string;
  link: string;
  taskOpen: boolean;
  notice: { outcome: string; reasonCode: string | null } | null;
}

/** A thrown transport error is "the server was never reached": no status, no code. */
async function call<T>(run: () => Promise<any>): Promise<SbResult<T>> {
  try {
    const r = await run();
    return (r || { success: false, error: 'No answer from the server.' }) as SbResult<T>;
  } catch {
    return { success: false, error: 'Could not reach the server.' };
  }
}

/** A fresh key per dialog opening; a retry of the same act reuses it. */
export const newIdempotencyKey = (): string => {
  const raw = typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  // The server accepts 8-120 characters of [A-Za-z0-9:_-].
  return `sb-${raw}`.replace(/[^A-Za-z0-9:_-]/g, '-').slice(0, 120);
};

export const sendBackApi = {
  preview: (path: string) => call<SendBackPreview>(() => apiRequest<SendBackPreview>('GET', path)),
  send: (path: string, body: SendBackBody, idempotencyKey: string) =>
    call<SendBackResult>(() => apiRequest<SendBackResult>('POST', path, body, { headers: { 'Idempotency-Key': idempotencyKey } })),
  /** The caller's own returned work (or, with `made`, what they sent back). */
  mine: (q: { direction: 'received' | 'made'; open?: boolean; flow?: SendBackFlow; limit?: number }) => {
    const p = new URLSearchParams({ direction: q.direction });
    if (q.open !== undefined) p.set('open', String(q.open));
    if (q.flow) p.set('flow', q.flow);
    if (q.limit) p.set('limit', String(q.limit));
    return call<{ direction: string; rows: ReturnedRow[] }>(() => apiRequest('GET', `/send-backs/mine?${p.toString()}`));
  },
  /** PA only: "Mark handled" closes the Returned-to-you task as DONE. No code needed. */
  markHandled: (caseId: string, actionId: string) =>
    call<{ actionId: string; status: string; replayed: boolean }>(() => apiRequest('POST', `/advocate/cases/${caseId}/returned/${actionId}/done`, {})),
};

/** The copy for each refusal the brief names. Any other code shows the server's message. */
export function refusalCopy(r: { code?: string; message?: string; error?: string; status?: number }): string {
  switch (r.code) {
    case 'STEP_UP_FAILED':
    case 'STEP_UP_REPLAYED':
      return 'That code did not work. Wait for a new code from your authenticator app and enter it.';
    case 'TOO_MANY_SEND_BACKS':
      return 'Too many send-backs in a short time. Wait a few minutes, then try again.';
    case 'NO_PREVIOUS_HOLDER':
      return "eMerge has no record of who held this before, so it can't be sent back automatically.";
    case 'STEP_UP_UNAVAILABLE':
      return 'Set up an authenticator first. Sending work back is signed with a code from your authenticator app.';
    case 'USE_SIGNED_SEND_BACK':
      return 'Returning to UMED now needs a signed send-back. Use Send back and confirm with your authenticator code.';
    case 'SEND_BACK_KEY_UNSET':
      return 'Send-back is not available yet.';
    default:
      if (r.status === undefined) return 'Could not reach the server. Nothing was sent back.';
      return r.message || r.error || 'The server refused this send-back.';
  }
}
